import { useCallback, useEffect } from 'react';
import { PanelControls } from '~/components/ui/PanelControls';
import { rage, useInputAction } from '~/rage';
import { InputAction, InputKey } from '~/shared';
import { useLayoutDispatch, useLayoutSelector } from '../../redux/hooks';
import {
	resetSettings,
	toggleFilter,
	toggleFormat,
	toggleGrid,
	toggleHorizontalLogoPosition,
	toggleInterfaceHidden,
	toggleVerticalLogoPosition,
} from '../../redux/slices/photoModeSlice';
import { StyledContainer, StyledLeftContainer } from './Controls.styled';

const Controls: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const filter = useLayoutSelector((state) => state.photoMode.filterActive);
	const format = useLayoutSelector((state) => state.photoMode.format);
	const gridVisible = useLayoutSelector((state) => state.photoMode.grid);

	useEffect(() => {
		rage.photoMode
			.swapFilter(filter)
			.catch((err) => rage.notifications.showException(err));
	}, [filter]);

	const handleToggleGrid = useCallback(() => {
		dispatch(toggleGrid());
	}, [dispatch]);

	const handleToggleFormat = useCallback(() => {
		dispatch(toggleFormat());
	}, [dispatch]);

	const handleToggleLogoPosition = useCallback(() => {
		if (format === 'horizontal') {
			dispatch(toggleHorizontalLogoPosition());
			return;
		}
		dispatch(toggleVerticalLogoPosition());
	}, [format, dispatch]);

	const handleToggleFilter = useCallback(() => {
		dispatch(toggleFilter());
	}, [dispatch]);

	const handleToggleInterfaceHidden = useCallback(() => {
		dispatch(toggleInterfaceHidden());
	}, []);

	const handleResetSettings = useCallback(() => {
		rage.photoMode
			.resetSettings()
			.catch((err) => rage.notifications.showException(err));
		dispatch(resetSettings());
	}, [dispatch]);

	const handleMenuClose = useCallback(() => {
		rage.photoMode.exit().catch((err) => rage.notifications.showException(err));
	}, []);

	const controlButtons = [
		{
			text: 'Управление',
			keys: [InputKey.W, InputKey.A, InputKey.S, InputKey.D],
		},
		{
			text: 'Наклонить',
			keys: [InputKey.Q, InputKey.E],
		},
		{
			text: 'Приблизить / Отдалить',
			keys: [InputKey.MouseMiddle],
		},
		{
			text: 'Поднять',
			keys: [InputKey.Space],
		},
		{
			text: 'Опустить',
			keys: [InputKey.Ctrl],
		},
		{
			text: 'Плавная камера',
			keys: [InputKey.Alt],
		},
	];

	const editButtons = [
		{
			text: 'Интерфейс Выкл',
			keys: [InputKey.I],
		},
		{
			text: 'Сменить фильтр',
			keys: [InputKey.F],
		},
		{
			text: 'Расположение лого',
			keys: [InputKey.C],
		},
		{
			text: 'Сменить формат',
			keys: [InputKey.N],
		},
		{
			text: `Сетка ${gridVisible ? 'Выкл' : 'Вкл'}`,
			keys: [InputKey.H],
		},
		{
			text: 'Сбросить настройки',
			keys: [InputKey.R],
		},
	];

	const exitButtons = [
		{
			text: 'Выйти',
			keys: ['Esc'],
		},
	];

	useInputAction(InputAction.PhotoModeToggleGrid, handleToggleGrid);
	useInputAction(InputAction.PhotoModeToggleFormat, handleToggleFormat);
	useInputAction(
		InputAction.PhotoModeToggleLogoPosition,
		handleToggleLogoPosition,
	);
	useInputAction(InputAction.PhotoModeToggleFilter, handleToggleFilter);
	useInputAction(
		InputAction.PhotoModeToggleInterfaceHidden,
		handleToggleInterfaceHidden,
	);
	useInputAction(InputAction.PhotoModeResetSettings, handleResetSettings);

	useInputAction(InputAction.CommonMenuClose, handleMenuClose);

	return (
		<StyledContainer>
			<StyledLeftContainer>
				<PanelControls controls={editButtons} align={'left'} />
				<PanelControls controls={controlButtons} align={'left'} />
			</StyledLeftContainer>
			<PanelControls controls={exitButtons} align={'right'} />
		</StyledContainer>
	);
};

export { Controls };
